import posthog from 'posthog-js';
import { syncOneSignalIdentity } from '@/components/entities/analytics/components/OneSIgnal/utils/oneSignal.client';
import { gtmUserProfileStore } from '@/components/entities/analytics/gtm';
import { authApi } from '@/queries/auth/auth.api';
import { QueriesKeys } from '@/queries/queriesKeys';
import { getQueryClient } from '@/queries/queryClient';
import { tokenManager } from '@/queries/token-manager';
import { checkToken } from '@/redux/slices/userSlice';
import { store } from '@/redux/store';

// list of private query keys to be cleared on logout
const LOGOUT_QUERY_KEYS = [
  [QueriesKeys.user],
  [QueriesKeys.verifications],
  [QueriesKeys.rewardsGrouped],
  [QueriesKeys.streakXpAwards, 'infinite'],
  [QueriesKeys.mePlayerStreak],
  [QueriesKeys.meRefereesSummary],
  [QueriesKeys.meReferral],
  [QueriesKeys.meRefereesAvailableRewardsCount],
  [QueriesKeys.mePlayerProfile],
  [QueriesKeys.mePlayerGamificationRewards],
  [QueriesKeys.mePlayerGamificationGroupedRewards],
  [QueriesKeys.meUserFreeSpins],
  [QueriesKeys.gamesWithWinsInfinite],
  [QueriesKeys.meReferees],
] as const;

type LogoutRedirectHandler = (url: string) => void;

type LogoutUserOptions = {
  redirectHomePage?: boolean;
  logoutAll?: boolean;
  redirectUrl?: string;
  onRedirect?: LogoutRedirectHandler;
};

const resolveLogoutOptions = (
  redirectHomePageOrOptions: boolean | LogoutUserOptions = false,
  logoutAll = false,
  redirectUrl?: string,
  onRedirect?: LogoutRedirectHandler,
): Required<Pick<LogoutUserOptions, 'redirectHomePage' | 'logoutAll'>> & Pick<LogoutUserOptions, 'redirectUrl' | 'onRedirect'> => {
  if (typeof redirectHomePageOrOptions === 'object') {
    return {
      redirectHomePage: redirectHomePageOrOptions.redirectHomePage ?? false,
      logoutAll: redirectHomePageOrOptions.logoutAll ?? false,
      redirectUrl: redirectHomePageOrOptions.redirectUrl,
      onRedirect: redirectHomePageOrOptions.onRedirect,
    };
  }

  return {
    redirectHomePage: redirectHomePageOrOptions,
    logoutAll,
    redirectUrl,
    onRedirect,
  };
};

export const logoutUser = async (
  redirectHomePageOrOptions: boolean | LogoutUserOptions = false,
  logoutAll = false,
  redirectUrl?: string,
  onRedirect?: LogoutRedirectHandler,
) => {
  const {
    redirectHomePage,
    logoutAll: shouldLogoutAll,
    redirectUrl: targetRedirectUrl,
    onRedirect: handleRedirect,
  } = resolveLogoutOptions(redirectHomePageOrOptions, logoutAll, redirectUrl, onRedirect);

  const redirectAfterLogout = () => {
    const targetUrl = targetRedirectUrl ?? (redirectHomePage ? '/' : '/?m=login');

    if (handleRedirect) {
      handleRedirect(targetUrl);
      return;
    }

    window.location.href = targetUrl;
  };

  const clearOneSignalIdentity = () => {
    void syncOneSignalIdentity(undefined).catch(() => {
      // ignore OneSignal logout errors during user logout flow
    });
  };

  const clearLogoutQueries = async () => {
    const queryClient = getQueryClient();

    await Promise.all(LOGOUT_QUERY_KEYS.map(queryKey => queryClient.cancelQueries({ queryKey: [...queryKey] })));
    LOGOUT_QUERY_KEYS.forEach(queryKey => queryClient.removeQueries({ queryKey: [...queryKey] }));
  };

  try {
    if (shouldLogoutAll) {
      await authApi.logoutAll();
    } else {
      await authApi.logout();
    }

    gtmUserProfileStore.clear();
    tokenManager.removeTokens();
    await clearLogoutQueries();
    localStorage.removeItem('currency');

    store().dispatch(checkToken());
    posthog.reset();
    clearOneSignalIdentity();

    window.cioanalytics?.identify(tokenManager.getUserId() || '', { subscription_status: false });

    redirectAfterLogout();
  } catch (error) {
    console.error('Logout failed', error);

    gtmUserProfileStore.clear();
    tokenManager.removeTokens();
    await clearLogoutQueries();
    store().dispatch(checkToken());
    posthog.reset();
    clearOneSignalIdentity();

    // Ensure we still navigate even if API logout fails
    redirectAfterLogout();
  }
};
